import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import type {
  OrganizationCustomField,
  Organization,
} from 'domain-model/types/supplier-gateway';

import {
  BooleanInput,
  DatePicker,
  NumberInput,
  Select,
  TextInput,
} from 'components/inputs';

const DefaultInput = (props: any) => {
  const { fieldType, ...rest } = props;
  return <TextInput {...rest} />;
};

interface Value {
  [key: string]: any;
}

interface CustomField {
  name: string;
  fieldType: 'string' | 'number' | 'boolean' | 'date' | 'select';
  localizedName: string;
  value: any;
  options: [{ value: string; label: string }];
}

const typeConverter = (value: string, fieldType: string) => {
  if (!value) {
    if (fieldType === 'boolean') {
      return false;
    }
    return null;
  }
  switch (fieldType) {
    case 'boolean':
      if (typeof value === 'string') {
        return value === 'true';
      }
      return value;
    case 'date':
      return new Date(value);
    case 'number':
      return _.toNumber(value);
    default:
      return value;
  }
};

const getCustomFields = (
  organization: Organization,
  value: Value,
  entityType: string,
) => {
  const result: CustomField[] = [];

  if (_.isEmpty(organization.organizationCustomFields)) {
    return result;
  }

  const organizationCustomFieldsForEntity =
    organization.organizationCustomFields.filter(
      (x: OrganizationCustomField) => x.entityType === entityType,
    );
  if (!organizationCustomFieldsForEntity.length) {
    return result;
  }

  organizationCustomFieldsForEntity.forEach(
    (customField: OrganizationCustomField) => {
      result.push({
        value: typeConverter(value?.[customField.name], customField.fieldType),
        name: customField.name,
        fieldType: customField.fieldType,
        localizedName: customField.localizedName,
        options: customField.selectOptions?.options || [],
      });
    },
  );

  return result;
};

interface CustomFieldsInputProps {
  value: Value;
  entityType: string;
  sessionUser: any;
  onChange: (name: string, value: Value) => void;
}

function CustomFieldsInput({
  value,
  entityType,
  sessionUser,
  onChange,
}: CustomFieldsInputProps) {
  const customFields = getCustomFields(
    sessionUser.organization,
    value,
    entityType,
  );

  const handleChange = (key: string, val: any) => {
    const newValue = { ...value, [key]: val };
    onChange('customFields', newValue);
  };

  const fieldInputs: Record<
    CustomField['fieldType'],
    React.ComponentType<any>
  > = {
    boolean: BooleanInput,
    date: DatePicker,
    number: NumberInput,
    string: TextInput,
    select: Select,
  };

  return (
    <div>
      {customFields.length > 0 &&
        customFields.map((x: CustomField, i: number) => {
          return (
            <Row key={x.name || i}>
              <Col xl={12}>
                <FormGroup>
                  <Label>{x.localizedName}</Label>

                  {React.createElement(
                    fieldInputs[x.fieldType as CustomField['fieldType']] ||
                      DefaultInput,
                    {
                      id: x.name,
                      name: x.name,
                      value: x.value,
                      onChange: handleChange,
                      options: x.options,
                    },
                    null,
                  )}
                </FormGroup>
              </Col>
            </Row>
          );
        })}
    </div>
  );
}
const mapStateToProps = (state: any) => ({
  sessionUser: state.authentication.sessionUser,
});
export default connect(mapStateToProps)(CustomFieldsInput);
